import { Reducer } from '@reduxjs/toolkit';
import { FluxStandardAction } from 'redux-promise-middleware';
import {
  BrandCustomField,
  CustomFieldGroup,
  CustomFieldIdName,
} from '../../../types/custom_fields';

export type CustomFieldToBrand = {
  custom_field_id: number;
  brand_ids: number[];
};

export type ParentCustomFieldsState = {
  customFields: BrandCustomField[];
  customFieldsIdNameList: CustomFieldIdName[];
  groups: CustomFieldGroup[];
  fieldToBrands: CustomFieldToBrand[];
  fetchingCustomFields: boolean;
  fetchingCustomFieldsIdNameList: boolean;
  fetchingGroups: boolean;
  fetchingFieldToBrands: boolean;
  fetchingCustomFieldUses: boolean;
  updatingCustomFieldToBrands: boolean;
  updatingCustomField: boolean;
  updatingGroups: boolean;
  deletingGroup: boolean;
  deletingCustomField: boolean;
  pendingChanges: Record<number, number[]>; // custom_field_id -> brand_ids
  customFieldUses: Record<number, boolean>;
};

const initialState: ParentCustomFieldsState = {
  customFields: [],
  customFieldsIdNameList: [],
  groups: [],
  fieldToBrands: [],
  fetchingCustomFields: false,
  fetchingCustomFieldsIdNameList: false,
  fetchingGroups: false,
  fetchingFieldToBrands: false,
  fetchingCustomFieldUses: false,
  updatingCustomFieldToBrands: false,
  updatingCustomField: false,
  updatingGroups: false,
  deletingGroup: false,
  deletingCustomField: false,
  pendingChanges: {},
  customFieldUses: {},
};

const reducer: Reducer<ParentCustomFieldsState, FluxStandardAction> = (
  state = initialState,
  action
) => {
  switch (action.type) {
    // Account Custom Fields
    case 'FETCH_ACCOUNT_CUSTOM_FIELDS_PENDING': {
      return { ...state, fetchingCustomFields: true };
    }
    case 'FETCH_ACCOUNT_CUSTOM_FIELDS_FULFILLED': {
      return {
        ...state,
        customFields: action.payload.data,
        fetchingCustomFields: false,
      };
    }
    case 'FETCH_ACCOUNT_CUSTOM_FIELDS_REJECTED': {
      return { ...state, fetchingCustomFields: false };
    }

    case 'FETCH_ACCOUNT_CUSTOM_FIELD_USES_PENDING': {
      return { ...state, fetchingCustomFieldUses: true };
    }
    case 'FETCH_ACCOUNT_CUSTOM_FIELD_USES_FULFILLED': {
      return {
        ...state,
        customFieldUses: action.payload.data,
        fetchingCustomFieldUses: false,
      };
    }
    case 'FETCH_ACCOUNT_CUSTOM_FIELD_USES_REJECTED': {
      return { ...state, fetchingCustomFieldUses: false };
    }

    // Account Custom Field Groups
    case 'FETCH_ACCOUNT_CUSTOM_FIELD_GROUPS_PENDING':
    case 'FETCH_CUSTOM_FIELD_GROUPS_PENDING': {
      return { ...state, fetchingGroups: true };
    }
    case 'FETCH_ACCOUNT_CUSTOM_FIELD_GROUPS_FULFILLED':
    case 'FETCH_CUSTOM_FIELD_GROUPS_FULFILLED': {
      return {
        ...state,
        groups: action.payload.data,
        fetchingGroups: false,
      };
    }
    case 'FETCH_ACCOUNT_CUSTOM_FIELD_GROUPS_REJECTED':
    case 'FETCH_CUSTOM_FIELD_GROUPS_REJECTED': {
      return { ...state, fetchingGroups: false };
    }

    // Custom Fields to Brands
    case 'FETCH_CUSTOM_FIELDS_BRANDS_PENDING': {
      return { ...state, fetchingFieldToBrands: true };
    }
    case 'FETCH_CUSTOM_FIELDS_BRANDS_FULFILLED': {
      return {
        ...state,
        fieldToBrands: action.payload.data,
        fetchingFieldToBrands: false,
      };
    }
    case 'FETCH_CUSTOM_FIELDS_BRANDS_REJECTED': {
      return { ...state, fetchingFieldToBrands: false };
    }

    // Update Custom Fields to Brands
    case 'UPDATE_CUSTOM_FIELD_TO_BRANDS_PENDING': {
      return { ...state, updatingCustomFieldToBrands: true };
    }
    case 'UPDATE_CUSTOM_FIELD_TO_BRANDS_FULFILLED': {
      // After successful update, clear pending changes
      // The API returns {"success": true}, so we don't update fieldToBrands here
      // Instead, we'll refetch the data in the container
      return {
        ...state,
        updatingCustomFieldToBrands: false,
        pendingChanges: {},
      };
    }
    case 'UPDATE_CUSTOM_FIELD_TO_BRANDS_REJECTED': {
      return { ...state, updatingCustomFieldToBrands: false };
    }

    // Local state management for pending changes
    case 'SET_PENDING_BRAND_CHANGES': {
      const { customFieldId, brandIds } = action.payload;
      return {
        ...state,
        pendingChanges: {
          ...state.pendingChanges,
          [customFieldId]: brandIds,
        },
      };
    }
    case 'SET_PENDING_BRAND_CHANGES_BATCH': {
      const { fieldToBrandMapping } = action.payload;
      return {
        ...state,
        pendingChanges: {
          ...state.pendingChanges,
          ...fieldToBrandMapping,
        },
      };
    }
    case 'CLEAR_PENDING_CHANGES': {
      return {
        ...state,
        pendingChanges: {},
      };
    }

    // Update Custom Field (create/edit)
    case 'UPDATE_PARENT_CUSTOM_FIELD_PENDING': {
      return { ...state, updatingCustomField: true };
    }
    case 'UPDATE_PARENT_CUSTOM_FIELD_FULFILLED': {
      // The API returns the updated/created custom field data
      const updatedField = action.payload.data;
      const isNewField = !action.meta.customFieldId;

      let updatedCustomFields;
      if (isNewField) {
        // Add new field to the array
        updatedCustomFields = [...state.customFields, updatedField];
      } else {
        // Update existing field in the array
        updatedCustomFields = state.customFields.map(field =>
          field.id === action.meta.customFieldId ? updatedField : field
        );
      }

      return {
        ...state,
        customFields: updatedCustomFields,
        updatingCustomField: false,
      };
    }
    case 'UPDATE_PARENT_CUSTOM_FIELD_REJECTED': {
      return { ...state, updatingCustomField: false };
    }

    // Update Custom Field Groups
    case 'UPDATE_PARENT_CUSTOM_FIELD_GROUPS_PENDING': {
      return { ...state, updatingGroups: true };
    }
    case 'UPDATE_PARENT_CUSTOM_FIELD_GROUPS_FULFILLED': {
      // Update the groups with the new data from the API
      const updatedGroups = action.payload.data;
      return {
        ...state,
        groups: updatedGroups,
        updatingGroups: false,
      };
    }
    case 'UPDATE_PARENT_CUSTOM_FIELD_GROUPS_REJECTED': {
      return { ...state, updatingGroups: false };
    }

    // Delete Custom Field Group
    case 'DELETE_PARENT_CUSTOM_FIELD_GROUP_PENDING': {
      return { ...state, deletingGroup: true };
    }
    case 'DELETE_PARENT_CUSTOM_FIELD_GROUP_FULFILLED': {
      // Remove the deleted group from the groups array
      const deletedGroupId = action.meta.groupId;
      const updatedGroups = state.groups.filter(group => group.id !== deletedGroupId);
      return {
        ...state,
        groups: updatedGroups,
        deletingGroup: false,
      };
    }
    case 'DELETE_PARENT_CUSTOM_FIELD_GROUP_REJECTED': {
      return { ...state, deletingGroup: false };
    }

    // Handle brand-level group deletion (also updates parent state)
    case 'DELETE_CUSTOM_FIELD_GROUP_FULFILLED': {
      // Remove the deleted group from the groups array
      const deletedGroupId = action.meta.groupId;
      const updatedGroups = state.groups.filter(group => group.id !== deletedGroupId);
      return {
        ...state,
        groups: updatedGroups,
      };
    }

    // Delete Custom Field
    case 'DELETE_BRAND_CUSTOM_FIELD_PENDING': {
      return { ...state, deletingCustomField: true };
    }
    case 'DELETE_BRAND_CUSTOM_FIELD_FULFILLED': {
      // Remove the deleted custom field from the customFields array
      const deletedCustomFieldId = action.meta.customFieldId;
      const updatedCustomFields = state.customFields.filter(
        field => field.id !== deletedCustomFieldId
      );
      return {
        ...state,
        customFields: updatedCustomFields,
        deletingCustomField: false,
      };
    }
    case 'DELETE_BRAND_CUSTOM_FIELD_REJECTED': {
      return { ...state, deletingCustomField: false };
    }

    // Keep old actions for the receiver view
    case 'FETCH_PARENT_CUSTOM_FIELDS_PENDING': {
      return { ...state, customFieldsIdNameList: [], fetchingCustomFieldsIdNameList: true };
    }
    case 'FETCH_PARENT_CUSTOM_FIELDS_FULFILLED': {
      return {
        ...state,
        customFieldsIdNameList: action.payload.data,
        fetchingCustomFieldsIdNameList: false,
      };
    }
  }
  return state;
};

export default reducer;
